import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionHeader } from "@/components/shared/Common";
import {
  Users2, MapPin, ShieldCheck, Lock, Car, Radio, HeartPulse,
  Award, Search, ChevronRight, Filter, Sparkles,
} from "lucide-react";

/** Small chip inferring team capability from the combined_kit list. */
function CapabilityChips({ kit }) {
  const has = (s) => (kit || []).some((k) => k.toLowerCase().includes(s));
  return (
    <div className="flex flex-wrap gap-1">
      {has("radio") || has("comm") ? <span className="chip"><Radio className="w-3 h-3" /> Comms</span> : null}
      {has("first aid") || has("medical") ? <span className="chip"><HeartPulse className="w-3 h-3" /> Medical</span> : null}
      {has("vehicle") || has("driv") || has("dash") ? <span className="chip"><Car className="w-3 h-3" /> Vehicles</span> : null}
      {has("psa") ? <span className="chip bg-blue-50 text-blue-700 border-blue-100">PSA</span> : null}
      {has("sia") ? <span className="chip bg-indigo-50 text-indigo-700 border-indigo-100">SIA</span> : null}
      {has("cpo") ? <span className="chip bg-gold-50 text-gold-600 border-gold-100">CPO</span> : null}
    </div>
  );
}

export default function PublicTeams() {
  const [teams, setTeams] = useState(null);
  const [q, setQ] = useState({ role: "", job_county: "", required_kit: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q.role) params.set("role", q.role);
      if (q.job_county) params.set("job_county", q.job_county);
      if (q.required_kit) params.set("required_kit", q.required_kit);
      const { data } = await api.get(`/public/teams?${params.toString()}`);
      setTeams(data || []);
    } catch { setTeams([]); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    if (!teams) return { count: 0, avgExp: 0, totalMembers: 0 };
    const count = teams.length;
    const avgExp = count === 0 ? 0 : Math.round(teams.reduce((s, t) => s + (t.combined_experience || 0), 0) / count);
    const totalMembers = teams.reduce((s, t) => s + (t.num_members || 0), 0);
    return { count, avgExp, totalMembers };
  }, [teams]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-10">
      {/* Header */}
      <div className="grid lg:grid-cols-3 gap-8 items-end">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 bg-brand-light text-brand border border-brand/20 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
            <Users2 className="w-3.5 h-3.5" /> Public teams directory
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold text-white tracking-tight leading-[1.05]">
            Browse ready-made cluster teams — no login required.
          </h1>
          <p className="text-white/70 mt-4 font-manrope text-base sm:text-lg max-w-2xl">
            Anonymised by design. See combined experience, kit, medical and vehicle capability upfront.
            Contact details unlock only after a company registers and requests a booking.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="card-flat p-4 text-center"><div className="text-3xl font-outfit font-bold text-white">{stats.count}</div><div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Active teams</div></div>
          <div className="card-flat p-4 text-center"><div className="text-3xl font-outfit font-bold text-white">{stats.totalMembers}</div><div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Verified members</div></div>
          <div className="card-flat p-4 text-center"><div className="text-3xl font-outfit font-bold text-white">{stats.avgExp}</div><div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Avg team yrs exp</div></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat p-5 grid md:grid-cols-4 gap-3 items-end" data-testid="teams-filters">
        <div>
          <Label className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Role</Label>
          <select value={q.role} onChange={(e) => setQ({ ...q, role: e.target.value })} className="w-full h-10 rounded-md border border-white/10 bg-[#0A0A0A] px-3 text-sm font-manrope" data-testid="teams-filter-role">
            <option value="">Any role</option>
            <option>Security Guard</option>
            <option>CPO / VPO</option>
            <option>Event Security</option>
            <option>Static Security</option>
            <option>Security Driver</option>
            <option>Supervisor</option>
            <option>Team Lead</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">County / region</Label>
          <Input value={q.job_county} onChange={(e) => setQ({ ...q, job_county: e.target.value })} placeholder="e.g., Dublin" data-testid="teams-filter-county" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Required kit</Label>
          <Input value={q.required_kit} onChange={(e) => setQ({ ...q, required_kit: e.target.value })} placeholder="e.g., Radio, First aid" data-testid="teams-filter-kit" />
        </div>
        <Button onClick={load} disabled={busy} className="bg-brand hover:bg-brand-hover text-white h-10 font-manrope font-semibold" data-testid="teams-apply-filters">
          <Search className="w-4 h-4 mr-2" /> {busy ? "Searching…" : "Apply filters"}
        </Button>
      </div>

      {/* Results */}
      {teams === null ? (
        <div className="text-white/60 font-manrope">Loading directory…</div>
      ) : teams.length === 0 ? (
        <div className="card-flat p-10 text-center" data-testid="teams-empty">
          <Users2 className="w-8 h-8 text-white/50 mx-auto mb-3" />
          <div className="font-outfit font-semibold text-white mb-1">No teams match those filters yet.</div>
          <div className="text-sm text-white/60 font-manrope">Try broader filters, or if you&apos;re a lead pro — create the first one.</div>
          <Button asChild className="mt-4 bg-brand hover:bg-brand-hover text-white"><Link to="/register/staff">Register &amp; lead a team</Link></Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {teams.map((t) => (
            <div key={t.id} className="card-flat p-5 space-y-3 border-l-4 border-l-brand" data-testid={`team-card-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="ss-id" data-testid={`team-name-${t.id}`}>{t.name || "Cluster Team"}</span>
                <span className="verified-chip"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>
              </div>
              {t.lead_secusource_id && (
                <div className="text-xs text-white/60 font-manrope">Lead: <span className="text-white font-semibold">{t.lead_secusource_id}</span></div>
              )}
              {t.description && <div className="text-sm text-white/70 font-manrope line-clamp-2">{t.description}</div>}

              <div className="grid grid-cols-3 gap-2 text-center text-xs font-outfit">
                <div className="bg-[#0E0E0E] rounded p-2">
                  <div className="text-white/60 uppercase tracking-widest">Members</div>
                  <div className="text-white font-bold text-base">{t.num_members || 0}</div>
                </div>
                <div className="bg-[#0E0E0E] rounded p-2">
                  <div className="text-white/60 uppercase tracking-widest">Combined exp</div>
                  <div className="text-white font-bold text-base">{t.combined_experience || 0}<span className="text-white/60 text-xs"> yrs</span></div>
                </div>
                <div className="bg-[#0E0E0E] rounded p-2">
                  <div className="text-white/60 uppercase tracking-widest">Vehicles</div>
                  <div className="text-white font-bold text-base">{t.vehicles ? "Yes" : "—"}</div>
                </div>
              </div>

              <CapabilityChips kit={t.combined_kit} />

              {(t.combined_languages || []).length > 0 && (
                <div className="flex flex-wrap gap-1 text-[11px] text-white/60 font-manrope">
                  {(t.combined_languages || []).slice(0, 5).map((l) => (
                    <span key={l} className="bg-[#0E0E0E] border border-white/10 rounded px-1.5 py-0.5">{l}</span>
                  ))}
                </div>
              )}

              {t.quality_badges?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {t.quality_badges.slice(0, 3).map((b) => (
                    <span key={b} className="chip bg-amber-50 text-amber-700 border-amber-100"><Award className="w-3 h-3" /> {b}</span>
                  ))}
                </div>
              )}

              <div className="lock-chip"><Lock className="w-3.5 h-3.5" /> Contact hidden until booking</div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button asChild size="sm" className="bg-brand hover:bg-brand-hover text-white flex-1">
                  <Link to="/register/company" data-testid={`team-book-${t.id}`}>Register to book</Link>
                </Button>
                <Button asChild size="sm" variant="outline" className="border-white/15">
                  <Link to="/cluster-teams" data-testid={`team-learn-${t.id}`}>How clusters work</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom CTA */}
      <div className="rounded-xl bg-navy-900 text-white p-8 flex flex-wrap items-center justify-between gap-6">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-gold-300 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Ready to unlock a team?
          </div>
          <div className="font-outfit font-bold text-2xl leading-snug">Register your company to see contact details and request a booking.</div>
          <p className="text-slate-300 font-manrope text-sm mt-2">One transparent fee — €1/hr per worker or 3% of the booking, whichever is lower. No subscriptions.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild className="bg-brand hover:bg-brand-hover text-white"><Link to="/register/company" data-testid="teams-cta-company">Register as a company <ChevronRight className="w-4 h-4 ml-1" /></Link></Button>
          <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10"><Link to="/pricing" data-testid="teams-cta-pricing">See pricing</Link></Button>
        </div>
      </div>
    </div>
  );
}
